import { useQuery } from '@tanstack/react-query';
import { Button } from './ui/Button';
import { Input } from './ui/Input';
import { Label } from './ui/Label';
import { apiFetch } from '../lib/api';
import {
  END_TARGET,
  MAX_ALERT_BODY_LENGTH,
  MAX_AB_PATHS,
  MAX_FIELD_VALUE_LENGTH,
  evenPercents,
  issuesFor,
  stepName,
  type FlowIssue,
  type Step,
} from '../lib/flowEdit';
import type { Segment } from '../types/api';

/** Props shared by the field groups of the K3 step types. */
export interface StepEditorProps {
  step: Step;
  /** Position of the step in the flow, and the whole flow (the later steps are the branch targets). */
  index: number;
  steps: Step[];
  issues: FlowIssue[];
  onPatch: (patch: Partial<Step>) => void;
}

const SELECT_CLASS = 'h-[42px] rounded-[9px] border border-zinc-300 px-3 text-sm';
const TEXTAREA_CLASS =
  'w-full rounded-[9px] border border-zinc-300 px-3 py-2 text-sm font-[inherit] text-text placeholder:text-muted focus-visible:border-accent-hover';

/** A problem shown right under its field. */
export function FieldError({
  issues,
  stepId,
  field,
}: {
  issues: FlowIssue[];
  stepId: string;
  field: string;
}) {
  const found = issuesFor(issues, stepId, field);
  if (found.length === 0) return null;
  return (
    <>
      {found.map((i) => (
        <p key={i.message} id={`err-${stepId}-${field}`} className="m-0 text-xs text-danger-text">
          {i.message}
        </p>
      ))}
    </>
  );
}

function invalid(issues: FlowIssue[], stepId: string, field: string): true | undefined {
  return issuesFor(issues, stepId, field).length > 0 ? true : undefined;
}

/** A select of where a branch goes: the later steps, or the end of the flow. */
export function TargetSelect({
  id,
  label,
  steps,
  index,
  value,
  onChange,
}: {
  id: string;
  label: string;
  steps: Step[];
  index: number;
  value: string;
  onChange: (target: string) => void;
}) {
  const later = steps.slice(index + 1);
  const known = value === END_TARGET || later.some((s) => s.id === value);
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={id}>{label}</Label>
      <select
        id={id}
        className={SELECT_CLASS}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        {known ? null : <option value={value}>Choose a step…</option>}
        {later.map((s, j) => (
          <option key={s.id} value={s.id}>
            {stepName(steps, index + 1 + j)}
          </option>
        ))}
        <option value={END_TARGET}>End the flow</option>
      </select>
    </div>
  );
}

type ConditionKind = 'segment' | 'ordered_since_entry' | 'opened' | 'clicked' | 'filter';

function conditionKind(condition: unknown): ConditionKind {
  const c = (condition ?? {}) as { kind?: string; event?: string };
  if (c.kind === 'engaged_previous_mail') return c.event === 'clicked' ? 'clicked' : 'opened';
  if (c.kind === 'segment' || c.kind === 'ordered_since_entry' || c.kind === 'filter') {
    return c.kind;
  }
  return 'ordered_since_entry';
}

export function SplitFields({ step, index, steps, issues, onPatch }: StepEditorProps) {
  const kind = conditionKind(step.condition);
  const segmentId = (step.condition as { segmentId?: string } | undefined)?.segmentId ?? '';
  const segments = useQuery({
    queryKey: ['segments'],
    queryFn: () => apiFetch<{ items: Segment[] }>('/segments'),
    enabled: kind === 'segment',
  });
  const items = segments.data?.items ?? [];
  const setKind = (next: ConditionKind) => {
    if (next === 'segment') onPatch({ condition: { kind: 'segment', segmentId: '' } });
    else if (next === 'opened' || next === 'clicked') {
      onPatch({ condition: { kind: 'engaged_previous_mail', event: next } });
    } else onPatch({ condition: { kind: 'ordered_since_entry' } });
  };
  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={`split-kind-${step.id}`}>Test</Label>
        <select
          id={`split-kind-${step.id}`}
          className={SELECT_CLASS}
          value={kind}
          onChange={(e) => setKind(e.target.value as ConditionKind)}
        >
          <option value="segment">Contact is in a segment</option>
          <option value="ordered_since_entry">Contact ordered since entering the flow</option>
          <option value="opened">Contact opened the previous email</option>
          <option value="clicked">Contact clicked the previous email</option>
          {kind === 'filter' ? (
            <option value="filter">Custom filter (edit in the API)</option>
          ) : null}
        </select>
      </div>
      {kind === 'filter' ? (
        <p className="m-0 text-xs text-muted">
          This step tests a custom filter. Edit in the API; it is kept as it is when you save.
        </p>
      ) : null}
      {kind === 'segment' ? (
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={`split-segment-${step.id}`}>Segment</Label>
          <select
            id={`split-segment-${step.id}`}
            className={SELECT_CLASS}
            value={segmentId}
            aria-invalid={invalid(issues, step.id, 'condition')}
            onChange={(e) => onPatch({ condition: { kind: 'segment', segmentId: e.target.value } })}
          >
            <option value="">Choose a segment…</option>
            {segmentId && !items.some((s) => s.id === segmentId) ? (
              <option value={segmentId}>
                {segments.isLoading ? 'Loading…' : 'Unknown segment'}
              </option>
            ) : null}
            {items.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
          <FieldError issues={issues} stepId={step.id} field="condition" />
        </div>
      ) : null}
      <div className="grid grid-cols-2 gap-3">
        <div className="flex flex-col gap-1.5">
          <TargetSelect
            id={`split-yes-${step.id}`}
            label="If yes, go to"
            steps={steps}
            index={index}
            value={String(step.yesTo ?? '')}
            onChange={(yesTo) => onPatch({ yesTo })}
          />
          <FieldError issues={issues} stepId={step.id} field="yesTo" />
        </div>
        <div className="flex flex-col gap-1.5">
          <TargetSelect
            id={`split-no-${step.id}`}
            label="If no, go to"
            steps={steps}
            index={index}
            value={String(step.noTo ?? '')}
            onChange={(noTo) => onPatch({ noTo })}
          />
          <FieldError issues={issues} stepId={step.id} field="noTo" />
        </div>
      </div>
    </div>
  );
}

interface PathValue {
  id: string;
  label: string;
  percent: number;
  to: string;
}

export function AbSplitFields({ step, index, steps, issues, onPatch }: StepEditorProps) {
  const paths = (Array.isArray(step.paths) ? step.paths : []) as PathValue[];
  const total = paths.reduce((sum, p) => sum + (typeof p.percent === 'number' ? p.percent : 0), 0);
  const setPaths = (next: PathValue[]) => onPatch({ paths: next });
  const patchPath = (j: number, patch: Partial<PathValue>) =>
    setPaths(paths.map((p, k) => (k === j ? { ...p, ...patch } : p)));
  const nextTarget = steps[index + 1]?.id ?? END_TARGET;

  const addPath = () => {
    if (paths.length >= MAX_AB_PATHS) return;
    const id =
      ['a', 'b', 'c', 'd'].find((c) => !paths.some((p) => p.id === c)) ?? `p${paths.length}`;
    const percents = evenPercents(paths.length + 1);
    setPaths(
      [...paths, { id, label: `Version ${id.toUpperCase()}`, percent: 0, to: nextTarget }].map(
        (p, k) => ({ ...p, percent: percents[k] as number }),
      ),
    );
  };
  const removePath = (j: number) => {
    if (paths.length <= 2) return;
    const gone = paths[j] as PathValue;
    const rest = paths.filter((_, k) => k !== j);
    // The removed path's share goes to the first remaining path, so the total stays what it was.
    setPaths(rest.map((p, k) => (k === 0 ? { ...p, percent: p.percent + gone.percent } : p)));
  };
  const sumIssue = issuesFor(issues, step.id, 'paths');

  return (
    <div className="flex flex-col gap-3">
      {paths.map((p, j) => (
        <div
          key={p.id}
          role="group"
          aria-label={`Path ${j + 1}, step ${index + 1}`}
          className="flex flex-col gap-2 rounded-[9px] border border-zinc-200 p-3"
        >
          <div className="flex items-end gap-2.5 flex-wrap">
            <div className="flex flex-col gap-1.5 flex-1 min-w-32">
              <Label htmlFor={`ab-label-${step.id}-${p.id}`}>Name of path {j + 1}</Label>
              <Input
                id={`ab-label-${step.id}-${p.id}`}
                value={p.label}
                aria-invalid={invalid(issues, step.id, `paths[${j}].label`)}
                onChange={(e) => patchPath(j, { label: e.target.value })}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor={`ab-percent-${step.id}-${p.id}`}>Share of path {j + 1} (%)</Label>
              <Input
                id={`ab-percent-${step.id}-${p.id}`}
                type="number"
                min={1}
                max={99}
                className="w-24"
                value={typeof p.percent === 'number' ? p.percent : 0}
                aria-invalid={invalid(issues, step.id, `paths[${j}].percent`)}
                onChange={(e) => patchPath(j, { percent: Number(e.target.value) })}
              />
            </div>
            {paths.length > 2 ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                aria-label={`Remove path ${j + 1}, step ${index + 1}`}
                onClick={() => removePath(j)}
              >
                Remove
              </Button>
            ) : null}
          </div>
          <FieldError issues={issues} stepId={step.id} field={`paths[${j}].label`} />
          <FieldError issues={issues} stepId={step.id} field={`paths[${j}].percent`} />
          <FieldError issues={issues} stepId={step.id} field={`paths[${j}].id`} />
          <TargetSelect
            id={`ab-to-${step.id}-${p.id}`}
            label={`Path ${j + 1} goes to`}
            steps={steps}
            index={index}
            value={String(p.to ?? '')}
            onChange={(to) => patchPath(j, { to })}
          />
          <FieldError issues={issues} stepId={step.id} field={`paths[${j}].to`} />
        </div>
      ))}
      <div className="flex items-center gap-3 flex-wrap">
        <span
          className={`text-sm font-medium ${total === 100 ? 'text-muted' : 'text-danger-text'}`}
          aria-live="polite"
        >
          Total: {total} %
        </span>
        {paths.length < MAX_AB_PATHS ? (
          <Button
            type="button"
            variant="secondary"
            size="sm"
            aria-label={`Add path, step ${index + 1}`}
            onClick={addPath}
          >
            Add path
          </Button>
        ) : null}
        <Button
          type="button"
          variant="ghost"
          size="sm"
          aria-label={`Split evenly, step ${index + 1}`}
          onClick={() => {
            const percents = evenPercents(paths.length);
            setPaths(paths.map((p, k) => ({ ...p, percent: percents[k] as number })));
          }}
        >
          Split evenly
        </Button>
      </div>
      {sumIssue.map((i) => (
        <p key={i.message} className="m-0 text-xs text-danger-text">
          {i.message}
        </p>
      ))}
    </div>
  );
}

export function UpdateContactFields({ step, issues, onPatch }: StepEditorProps) {
  const action = typeof step.action === 'string' ? step.action : 'suppress_inactive';
  // Switching the action clears the fields of the old one, so nothing stale is saved.
  const setAction = (next: string) =>
    onPatch({
      action: next,
      key: next === 'set_field' ? '' : undefined,
      value: next === 'set_field' ? '' : undefined,
      label: next === 'add_label' || next === 'remove_label' ? '' : undefined,
    });
  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={`uc-action-${step.id}`}>Change</Label>
        <select
          id={`uc-action-${step.id}`}
          className={SELECT_CLASS}
          value={action}
          onChange={(e) => setAction(e.target.value)}
        >
          <option value="add_label">Add a tag</option>
          <option value="remove_label">Remove a tag</option>
          <option value="set_field">Set a contact field</option>
          <option value="suppress_inactive">Stop sending to the contact (inactive)</option>
        </select>
      </div>
      {action === 'set_field' ? (
        <div className="grid grid-cols-2 gap-3">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={`uc-key-${step.id}`}>Field key</Label>
            <Input
              id={`uc-key-${step.id}`}
              value={typeof step.key === 'string' ? step.key : ''}
              aria-invalid={invalid(issues, step.id, 'key')}
              onChange={(e) => onPatch({ key: e.target.value })}
            />
            <FieldError issues={issues} stepId={step.id} field="key" />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={`uc-value-${step.id}`}>Value</Label>
            <Input
              id={`uc-value-${step.id}`}
              maxLength={MAX_FIELD_VALUE_LENGTH + 50}
              value={typeof step.value === 'string' ? step.value : ''}
              aria-invalid={invalid(issues, step.id, 'value')}
              onChange={(e) => onPatch({ value: e.target.value })}
            />
            <FieldError issues={issues} stepId={step.id} field="value" />
          </div>
        </div>
      ) : null}
      {action === 'add_label' || action === 'remove_label' ? (
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={`uc-label-${step.id}`}>Tag</Label>
          <Input
            id={`uc-label-${step.id}`}
            value={typeof step.label === 'string' ? step.label : ''}
            aria-invalid={invalid(issues, step.id, 'label')}
            onChange={(e) => onPatch({ label: e.target.value })}
          />
          <FieldError issues={issues} stepId={step.id} field="label" />
        </div>
      ) : null}
      {action === 'suppress_inactive' ? (
        <p className="m-0 text-xs text-muted">
          The contact is marked inactive and gets no more email.
        </p>
      ) : null}
    </div>
  );
}

export function AlertFields({ step, issues, onPatch }: StepEditorProps) {
  return (
    <div className="flex flex-col gap-3">
      <p className="m-0 text-xs text-muted">
        Sends a short plain-text email to a team member, never to the contact. No contact details
        are added; {'{{flow_name}}'} and {'{{step_id}}'} are filled in. The address must belong to a
        member of this workspace.
      </p>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={`alert-to-${step.id}`}>Send to</Label>
        <Input
          id={`alert-to-${step.id}`}
          type="email"
          value={typeof step.to === 'string' ? step.to : ''}
          aria-invalid={invalid(issues, step.id, 'to')}
          onChange={(e) => onPatch({ to: e.target.value })}
        />
        <FieldError issues={issues} stepId={step.id} field="to" />
      </div>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={`alert-subject-${step.id}`}>Alert subject</Label>
        <Input
          id={`alert-subject-${step.id}`}
          value={typeof step.subject === 'string' ? step.subject : ''}
          aria-invalid={invalid(issues, step.id, 'subject')}
          onChange={(e) => onPatch({ subject: e.target.value })}
        />
        <FieldError issues={issues} stepId={step.id} field="subject" />
      </div>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={`alert-body-${step.id}`}>Alert message</Label>
        <textarea
          id={`alert-body-${step.id}`}
          rows={3}
          className={TEXTAREA_CLASS}
          value={typeof step.body === 'string' ? step.body : ''}
          aria-invalid={invalid(issues, step.id, 'body')}
          onChange={(e) => onPatch({ body: e.target.value })}
        />
        <span className="text-xs text-muted">
          {(typeof step.body === 'string' ? step.body : '').length} / {MAX_ALERT_BODY_LENGTH}
        </span>
        <FieldError issues={issues} stepId={step.id} field="body" />
      </div>
    </div>
  );
}

export function WebhookFields({ step, issues, onPatch }: StepEditorProps) {
  return (
    <div className="flex flex-col gap-3">
      <p className="m-0 text-xs text-muted">
        Sends a JSON message to the address when a contact reaches this step. https only, to a
        public host name; no login details are sent and the answer is not shown.
      </p>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={`webhook-url-${step.id}`}>Webhook address</Label>
        <Input
          id={`webhook-url-${step.id}`}
          type="url"
          placeholder="https://"
          value={typeof step.url === 'string' ? step.url : ''}
          aria-invalid={invalid(issues, step.id, 'url')}
          onChange={(e) => onPatch({ url: e.target.value })}
        />
        <FieldError issues={issues} stepId={step.id} field="url" />
      </div>
    </div>
  );
}

export function EndFields() {
  return <p className="m-0 text-sm text-muted">The flow ends here for the contact.</p>;
}
